
import {useReducer} from 'react';
import {ApolloClient, useApolloClient} from '@apollo/client';
import moment from 'moment-timezone';
import {sortkey} from 'utils/sort';

import {getImages, Images, Result, Vars} from './query';

const maxParallelRunners = 4;
const maxImagesPerRequest = 1;

export type State = {
	client: ApolloClient<object>
	dispatch: React.Dispatch<Action>
	token: string
	inflight: number
	remaining: string[]
	images: Images[]
};

type Action =
  | { type: 'SET-DATA', data: Result, token: string }
  | { type: 'SET-DISPATCH', dispatch: React.Dispatch<Action> }
  | { type: 'SET-VARS', vars: Vars }
  | { type: 'QUERY-NEXT' }
  | { type: 'COUNT-INC' }
  | { type: 'COUNT-DEC' }
;

const increment = (dispatch: React.Dispatch<Action>) => {
	dispatch({type: 'COUNT-INC'});
	return () => dispatch({type: 'COUNT-DEC'});
};

const reducer = (state: State, action: Action): State => {
	switch (action.type) {

	// SET-DATA aggregates edges from query results onto state
	case 'SET-DATA': {
		if (action.token !== state.token) {
			return {...state};
		}

		// Append images & sort; latest first
		const images = [
			...state.images,
			...action.data.reports.dexaImages,
		]
			.filter((image) => image)
			.sort(sortkey((image) => -image.start.unix));

		return {
			...state,
			images,
		};
	}

	// SET-DISPATCH is called close to initialization
	// and sets the dispatch function on the state object
	case 'SET-DISPATCH':
		return {...state, dispatch: action.dispatch};

	// SET-VARS clears our current state and sets a
	// new region/slug & token to handle any in-flight requests
	case 'SET-VARS':
		for (let i = 0; i < maxParallelRunners; i++) {
			state.dispatch({type: 'QUERY-NEXT'});
		}

		return {
			...state,
			token: moment().unix().toString(),
			remaining: [...action.vars.appts],
			images: [],
		};

	// QUERY-NEXT checks if there is a remaining request
	// and if so, processes it by generating a parallel query
	case 'QUERY-NEXT':
		if (state.remaining.length > 0) {
			const decrement = increment(state.dispatch);
			const batch = state.remaining.slice(0, maxImagesPerRequest);
			const remaining = state.remaining.slice(maxImagesPerRequest);

			getImages(state.client, batch).then(({data}) => {
				// Add our most recent result to the state
				state.dispatch({type: 'SET-DATA', data, token: state.token});

				// Trigger an additional following request
				state.dispatch({type: 'QUERY-NEXT'});
			}).catch(() => {
				return;
			}).finally(() => {
				decrement();
			});

			return {
				...state,
				remaining,
			};
		}

		// If no remainings, exit without changes
		return {...state};

	case 'COUNT-INC':
		return {
			...state,
			inflight: state.inflight + 1,
		};

	case 'COUNT-DEC':
		return {
			...state,
			inflight: state.inflight - 1,
		};
	}
};

const initState = (client: ApolloClient<object>): State => {
	return {
		client,
		dispatch: () => null,
		token: '',
		inflight: 0,
		remaining: [],
		images: [],
	};
};

export const useState = () => {
	const client = useApolloClient();
	return useReducer(reducer, client, initState);
};
